import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Phone, User, MapPin, Loader2, GripVertical, KanbanSquare, Clock } from "lucide-react";

const COLUMN_TONES = {
  pending:      "bg-stone-50 border-stone-300",
  screening:    "bg-sky-50 border-sky-300",
  shortlisted:  "bg-amber-50 border-amber-300",
  interview:    "bg-purple-50 border-purple-300",
  offered:      "bg-emerald-50 border-emerald-300",
  hired:        "bg-green-50 border-green-400",
  rejected:     "bg-red-50 border-red-300",
};

// Stages where "stuck" is a real concern; hired/rejected are terminal.
const URGENCY_STAGES = new Set(["pending", "screening", "shortlisted", "interview", "offered"]);

function stageAgeBadge(card) {
  const ts = card.stage_updated_at || card.created_at;
  if (!ts) return null;
  const ms = Date.now() - new Date(ts).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor(ms / 3600000);
  const label = days >= 1 ? `${days}d` : `${Math.max(1, hours)}h`;
  const urgent = days >= 3;
  return { label, urgent, days };
}

export default function Pipeline() {
  const [data, setData] = useState(null);
  const [dragging, setDragging] = useState(null); // {appId, fromStage}
  const [dragOver, setDragOver] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pipeline");
      setData(data);
    } catch (e) { toast.error(formatError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalApplicants = useMemo(() => {
    if (!data) return 0;
    return Object.values(data.by_stage).reduce((n, arr) => n + arr.length, 0);
  }, [data]);

  const moveCard = async (appId, fromStage, toStage) => {
    if (fromStage === toStage) return;
    // Optimistic update
    setData((prev) => {
      if (!prev) return prev;
      const next = { ...prev, by_stage: { ...prev.by_stage } };
      const card = (next.by_stage[fromStage] || []).find((c) => c.id === appId);
      if (!card) return prev;
      next.by_stage[fromStage] = next.by_stage[fromStage].filter((c) => c.id !== appId);
      next.by_stage[toStage] = [{ ...card, status: toStage }, ...(next.by_stage[toStage] || [])];
      return next;
    });
    setBusy(true);
    try {
      await api.patch(`/applications/${appId}/stage`, { status: toStage });
      toast.success(`Moved to ${data.labels[toStage]}`);
    } catch (e) {
      toast.error(formatError(e));
      load(); // revert on failure
    } finally { setBusy(false); }
  };

  const onDragStart = (e, card, stage) => {
    setDragging({ appId: card.id, fromStage: stage });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", card.id);
  };
  const onDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOver !== stage) setDragOver(stage);
  };
  const onDrop = (e, toStage) => {
    e.preventDefault();
    setDragOver("");
    if (!dragging) return;
    const { appId, fromStage } = dragging;
    setDragging(null);
    moveCard(appId, fromStage, toStage);
  };

  if (!data) return <div className="p-10 text-center text-sarthi-muted">Loading pipeline…</div>;

  return (
    <div className="mx-auto max-w-[1600px] px-3 py-6 sm:px-6" data-testid="pipeline-page">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">
            <KanbanSquare className="h-8 w-8 text-sarthi-primary" /> Recruitment pipeline
          </h1>
          <p className="mt-1 text-base font-medium text-sarthi-muted">
            Drag candidates between stages — they're auto-notified via WhatsApp/SMS.
          </p>
        </div>
        <div className="rounded-full bg-sarthi-accent px-4 py-2 text-sm font-extrabold text-sarthi-primary">
          {totalApplicants} total applicant{totalApplicants === 1 ? "" : "s"}
        </div>
      </div>

      {busy && (
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-sarthi-muted">
          <Loader2 className="h-3 w-3 animate-spin" /> Saving…
        </div>
      )}

      <div className="flex gap-3 overflow-x-auto pb-4">
        {data.stages.map((stage) => {
          const cards = data.by_stage[stage] || [];
          const tone = COLUMN_TONES[stage] || "bg-stone-50 border-stone-300";
          const isDragOver = dragOver === stage;
          return (
            <div
              key={stage}
              data-testid={`pipeline-col-${stage}`}
              onDragOver={(e) => onDragOver(e, stage)}
              onDragLeave={() => setDragOver("")}
              onDrop={(e) => onDrop(e, stage)}
              className={`flex min-w-[280px] flex-1 flex-col rounded-2xl border-2 ${tone} ${isDragOver ? "ring-4 ring-sarthi-primary/40" : ""}`}
            >
              <div className="flex items-center justify-between px-4 pb-2 pt-4">
                <div className="font-display text-base font-extrabold uppercase tracking-wide text-sarthi-text">
                  {data.labels[stage]}
                </div>
                <div className="rounded-full bg-white px-2.5 py-0.5 text-xs font-extrabold text-sarthi-muted shadow-sm">
                  {cards.length}
                </div>
              </div>
              <div className="flex-1 space-y-2 p-3 pt-1">
                {cards.length === 0 && (
                  <div className="rounded-xl border-2 border-dashed border-sarthi-border/50 bg-white/50 py-6 text-center text-xs font-semibold text-sarthi-muted">
                    Drop here
                  </div>
                )}
                {cards.map((card) => {
                  const badge = URGENCY_STAGES.has(stage) ? stageAgeBadge(card) : null;
                  return (
                  <div
                    key={card.id}
                    data-testid={`pipeline-card-${card.id}`}
                    draggable
                    onDragStart={(e) => onDragStart(e, card, stage)}
                    onDragEnd={() => setDragging(null)}
                    className={`group cursor-grab rounded-xl border-2 bg-white p-3 shadow-sm hover:border-sarthi-primary/50 active:cursor-grabbing ${badge?.urgent ? "border-red-300 ring-1 ring-red-200" : "border-white"}`}
                  >
                    <div className="flex items-start gap-2">
                      <GripVertical className="h-4 w-4 flex-shrink-0 text-stone-400" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-1 truncate font-bold text-sarthi-text">
                            <User className="h-3.5 w-3.5 flex-shrink-0 text-sarthi-primary" />
                            <span className="truncate">{card.worker_name || "Anonymous"}</span>
                          </div>
                          {badge && (
                            <span
                              data-testid={`stage-age-${card.id}`}
                              title={`${badge.days} day${badge.days === 1 ? "" : "s"} in ${data.labels[stage]}`}
                              className={`inline-flex flex-none items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide ${badge.urgent ? "bg-red-100 text-red-700 ring-1 ring-red-300" : "bg-stone-100 text-stone-600"}`}
                            >
                              <Clock className="h-3 w-3" strokeWidth={3} /> {badge.label}
                            </span>
                          )}
                        </div>
                        <Link
                          to={`/contractor/jobs/${card.job_id}`}
                          className="mt-1 block truncate text-xs font-semibold text-sarthi-primary hover:underline"
                        >
                          {card.job_title}
                        </Link>
                        {card.job_location && (
                          <div className="mt-1 flex items-center gap-1 truncate text-xs font-medium text-sarthi-muted">
                            <MapPin className="h-3 w-3" /> {card.job_location}
                          </div>
                        )}
                        {card.worker_mobile && (
                          <a
                            href={`tel:${card.worker_mobile}`}
                            className="mt-1 flex items-center gap-1 text-xs font-bold text-sarthi-secondary hover:underline"
                          >
                            <Phone className="h-3 w-3" /> {card.worker_mobile}
                          </a>
                        )}
                        <div className="mt-1 text-xs text-sarthi-muted">
                          {card.worker_experience || 0} yr exp
                          {card.worker_skills?.length ? ` · ${card.worker_skills.slice(0, 2).join(", ")}` : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
